import type { ApiOrgContext } from "@/lib/api/core";
import { createOrgQuery } from "@/lib/api/hook-factory";
import { titleCaseWords } from "@/lib/string-utils";
import type { AppliedView } from "./views-data";

interface ManageViewsListResult {
	views?: Array<{
		key?: unknown;
		name?: unknown;
		description?: unknown;
		attach?: unknown;
		params?: unknown;
		content_hash?: unknown;
	}>;
}

/** The org's applied views (`manage_views list`): key, name, description, attach, params and
 *  content hash only — the server projects source and bundle off the list shape. Stale
 *  for 10 s; a `view:<key>` SSE invalidation refreshes it sooner (see
 *  `use-invalidation-sse`). Read-only from the browser: views are authored in
 *  files or chat, never in-app (config-owned rows are read-only by the
 *  declared/unmanaged rule). */
export const useAppliedViews = createOrgQuery<
	[orgContext: ApiOrgContext],
	AppliedView[]
>({
	// Slug only, and the key is that same slug: an organization id would let
	// the request resolve against whatever org the address bar names by then,
	// filing one org's views under another's cache key.
	orgContext: (orgContext) => ({ slug: orgContext.slug ?? null }),
	queryKey: (ctx) => ["views", ctx.slug],
	tool: "manage_views",
	body: () => ({ action: "list" }),
	transform: (r: ManageViewsListResult) =>
		(Array.isArray(r.views) ? r.views : []).flatMap((v): AppliedView[] => {
			if (!v || typeof v.key !== "string" || typeof v.name !== "string")
				return [];
			return [
				{
					key: v.key,
					name:
						v.name === v.key
							? titleCaseWords(v.key.replaceAll("-", " "))
							: v.name,
					description:
						typeof v.description === "string" && v.description.trim()
							? v.description.trim()
							: undefined,
					attach: Array.isArray(v.attach) ? v.attach : [],
					params:
						v.params && typeof v.params === "object" && !Array.isArray(v.params)
							? (v.params as AppliedView["params"])
							: undefined,
					content_hash:
						typeof v.content_hash === "string" ? v.content_hash : null,
				},
			];
		}),
	// Any org the URL names: a public workspace publishes its view list so a
	// visitor gets the hub tabs and can open a view (the server admits the read
	// only when the workspace is `public` and answers 403 otherwise). Gating on
	// the member org id left a public workspace's tabs and view blank forever —
	// the query never ran, so `data` never resolved.
	enabled: (ctx) => !!ctx.slug,
	staleTime: 10_000,
});
